import { useState, useEffect, useRef } from "react";
import projects from "../projects";

/* ───────────────────────── Data ───────────────────────── */
const skills = [
  {
    icon: "fa-solid fa-code",
    title: "Languages",
    items: ["C", "C++", "Python", "Java", "R"],
  },
  {
    icon: "fa-solid fa-globe",
    title: "Web & Frameworks",
    items: [
      "React",
      "Electron",
      "Flask",
      "FastAPI",
      "HTML",
      "CSS",
      "JavaScript",
      "Bootstrap",
      "MongoDB",
      "SQL",
    ],
  },
  {
    icon: "fa-solid fa-tools",
    title: "Tools & Platforms",
    items: ["Git", "Linux", "Vercel", "Electron", "Qt", "Yocto"],
  },
  {
    icon: "fa-solid fa-microchip",
    title: "Core CS",
    items: [
      "Data Structures",
      "Algorithms",
      "Operating Systems",
      "Networking",
      "Low-Level Design",
      "Embedded Systems",
    ],
  },
];

const experienceBullets = [
  "Architected the frontend for a React + Electron-based AI training application deployed to 50+ edge devices, engineering a robust Pause/Resume mechanism handling complex state synchronization and concurrency.",
  "Developed a RAG-based text analysis interface supporting file uploads and real-time token streaming, optimizing asynchronous error handling for non-blocking UI updates during heavy inference.",
  "Slashed subsequent application launch times by 92% (2.5 min → 12 sec) by designing a persistent state-tracking marker file to bypass redundant package installations.",
  "Orchestrated end-to-end frontend workflow for audio model training — from dataset selection to annotation to training — creating a seamless multi-step user journey with centralized state validation.",
  "Implemented an automated CUDA installation process, increasing GPU-accelerated training efficiency by 40% on compatible systems.",
  "Designed and delivered an internal regression dashboard for Cisco Systems using Flask + React, improving test result visibility for QA teams by 40%.",
  "Engineered a Qt-based WLAN connectivity UI on Yocto embedded Linux for the Synaptics Astra board; enabled WPA2/WPA3 authentication and improved network stability by 35%.",
  "Developed a Qt-based Bluetooth manager for the Synaptics Astra board with 99% pairing reliability.",
];

const achievements = [
  { icon: "fa-solid fa-award", text: "GATE 2024 (CS) Qualified" },
  {
    icon: "fa-solid fa-trophy",
    text: "AMCAT 99th Percentile — Programming & Problem Solving",
  },
  {
    icon: "fa-solid fa-code",
    text: "LeetCode Rating 1776 · CodeChef 3-Star",
  },
  {
    icon: "fa-solid fa-medal",
    text: "NPTEL Discipline Star (Computer Science)",
  },
  {
    icon: "fa-solid fa-star",
    text: "Best Final Year Project Award (2024) · Best Mini-Project Award (2021)",
  },
  {
    icon: "fa-solid fa-people-group",
    text: "Organizer — Code Mantra (Gyan Mitra 2022), national-level symposium",
  },
];

/* ──────────────────── Intersection Observer Hook ────────────────── */
function useFadeIn() {
  const ref = useRef();
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      },
      { threshold: 0.15 }
    );
    const node = ref.current;
    if (node) observer.observe(node);
    return () => node && observer.unobserve(node);
  }, []);
  return ref;
}

function FadeIn({ children, className = "", style }) {
  const ref = useFadeIn();
  return (
    <div ref={ref} className={`fade-in ${className}`} style={style}>
      {children}
    </div>
  );
}

/* ───────────────────────── App ─────────────────────────── */
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Animated BG */}
      <div className="bg-grid" aria-hidden="true" />

      {/* ─────── Navbar ─────── */}
      <nav className={`navbar${scrolled ? " scrolled" : ""}`}>
        <div className="container">
          <div className="navbar__logo">
            Arun<span>kumar</span>
          </div>

          <button
            className="navbar__toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
          >
            <span />
            <span />
            <span />
          </button>

          <div className={`navbar__links${menuOpen ? " open" : ""}`}>
            {["about", "experience", "skills", "projects", "education", "contact"].map(
              (s) => (
                <a key={s} href={`#${s}`} onClick={() => scrollTo(s)}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </a>
              )
            )}
            <a
              href="/Arunkumar49r/Resume-main.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--outline btn--sm"
            >
              Resume
            </a>
          </div>
        </div>
      </nav>

      {/* ─────── Hero ─────── */}
      <section className="hero" id="hero">
        <div className="container">
          <div className="hero__content">
            <p className="hero__greeting">Hi, my name is</p>
            <h1 className="hero__name">Arunkumar R.</h1>
            <h2 className="hero__title">Software Engineer</h2>
            <p className="hero__bio">
              I build scalable, efficient solutions across full-stack and
              embedded domains — from React + Electron desktop apps deployed to
              edge devices, to Qt interfaces on embedded Linux. Currently
              engineering AI-enabled applications at{" "}
              <strong style={{ color: "var(--accent-primary)" }}>
                EmbedUR Systems
              </strong>
              .
            </p>
            <div className="hero__actions">
              <a href="#contact" className="btn btn--primary">
                <i className="fa-solid fa-paper-plane" /> Get in Touch
              </a>
              <a
                href="/Arunkumar49r/Resume-main.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--outline"
              >
                <i className="fa-solid fa-file-lines" /> View Resume
              </a>
            </div>
          </div>
          <div className="hero__glow" aria-hidden="true" />
        </div>
      </section>

      {/* ─────── About ─────── */}
      <section id="about">
        <div className="container">
          <FadeIn>
            <p className="section__label">About Me</p>
            <h3 className="section__title">A quick intro.</h3>
            <div className="section__divider" />
          </FadeIn>

          <div className="about__content">
            <FadeIn className="about__text">
              <p>
                Enthusiastic Software Engineer with hands-on experience in
                full-stack development and embedded systems. I have a strong
                foundation in data structures, algorithms, and system-level
                programming, with a proven ability to deliver scalable,
                efficient solutions in complex software environments.
              </p>
              <p>
                I specialize in bridging hardware and software workflows —
                particularly in developing AI-enabled desktop applications and
                embedded device integrations. I'm motivated to continuously
                learn emerging technologies and contribute to impactful
                projects that solve real-world problems.
              </p>
            </FadeIn>

            <FadeIn className="about__highlights">
              <div className="highlight-card">
                <div className="highlight-card__value">2+</div>
                <div className="highlight-card__label">Years of Experience</div>
              </div>
              <div className="highlight-card">
                <div className="highlight-card__value">50+</div>
                <div className="highlight-card__label">Edge Devices Deployed</div>
              </div>
              <div className="highlight-card">
                <div className="highlight-card__value">92%</div>
                <div className="highlight-card__label">Launch Time Reduced</div>
              </div>
              <div className="highlight-card">
                <div className="highlight-card__value">1776</div>
                <div className="highlight-card__label">LeetCode Rating</div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─────── Experience ─────── */}
      <section id="experience">
        <div className="container">
          <FadeIn>
            <p className="section__label">Experience</p>
            <h3 className="section__title">Where I've worked.</h3>
            <div className="section__divider" />
          </FadeIn>

          <FadeIn>
            <div className="experience__card">
              <div className="experience__header">
                <div>
                  <div className="experience__role">Software Engineer</div>
                  <div className="experience__company">EmbedUR Systems</div>
                </div>
                <div className="experience__meta">
                  <div className="location">
                    <i className="fa-solid fa-location-dot" /> Chennai, Tamil Nadu
                  </div>
                  <div className="date">Mar 2024 — Present</div>
                </div>
              </div>

              <div className="experience__bullets">
                {experienceBullets.map((b, i) => (
                  <div className="experience__bullet" key={i}>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─────── Skills ─────── */}
      <section id="skills">
        <div className="container">
          <FadeIn>
            <p className="section__label">Skills</p>
            <h3 className="section__title">Technologies I work with.</h3>
            <div className="section__divider" />
          </FadeIn>

          <div className="skills__grid">
            {skills.map((cat, i) => (
              <FadeIn key={i} style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="skill-category">
                  <div className="skill-category__icon">
                    <i className={cat.icon} />
                  </div>
                  <div className="skill-category__title">{cat.title}</div>
                  <div className="skill-tags">
                    {cat.items.map((item) => (
                      <span className="skill-tag" key={item}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── Projects ─────── */}
      <section id="projects">
        <div className="container">
          <FadeIn>
            <p className="section__label">Projects</p>
            <h3 className="section__title">Things I've built.</h3>
            <div className="section__divider" />
          </FadeIn>

          <div className="projects__grid">
            {projects.map((proj, i) => (
              <FadeIn key={proj.id} style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="project-card">
                  <div className="project-card__header">
                    <i className="fa-regular fa-folder-open project-card__folder" />
                    <div className="project-card__links">
                      {proj.repo && (
                        <a
                          href={proj.repo}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="GitHub"
                        >
                          <i className="fa-brands fa-github" />
                        </a>
                      )}
                      {proj.link && (
                        <a
                          href={proj.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Live Demo"
                        >
                          <i className="fa-solid fa-up-right-from-square" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="project-card__category">{proj.category}</div>
                  <div className="project-card__title">{proj.title}</div>
                  <p className="project-card__desc">{proj.description}</p>
                  <div className="project-card__tech">
                    {proj.used.map((tech) => (
                      <span key={tech}>{tech}</span>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── Education ─────── */}
      <section id="education">
        <div className="container">
          <FadeIn>
            <p className="section__label">Education</p>
            <h3 className="section__title">Academic background.</h3>
            <div className="section__divider" />
          </FadeIn>

          <div className="education__content">
            <FadeIn>
              <div className="education__card">
                <div className="education__degree">
                  B.E. in Electronics and Communication
                </div>
                <div className="education__school">
                  Mepco Schlenk Engineering College
                </div>
                <div className="education__detail">
                  <span>Sivakasi, Tamil Nadu</span>
                  <span>2020 — 2024</span>
                </div>
                <div
                  className="education__detail"
                  style={{ marginTop: 8, color: "var(--accent-cyan)" }}
                >
                  <span>CGPA: 8.1 / 10</span>
                </div>
              </div>
            </FadeIn>

            <FadeIn>
              <div style={{ marginBottom: 16 }}>
                <p
                  className="section__label"
                  style={{ marginBottom: 20, fontSize: "0.8rem" }}
                >
                  Achievements & Co-Curricular
                </p>
                <div className="achievements__list">
                  {achievements.map((a, i) => (
                    <div className="achievement" key={i}>
                      <i className={`${a.icon} achievement__icon`} />
                      <span>{a.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─────── Contact ─────── */}
      <section id="contact" className="contact">
        <div className="container">
          <FadeIn>
            <p className="section__label">Contact</p>
            <h3 className="section__title">Let's connect.</h3>
            <p className="section__subtitle">
              I'm currently open to opportunities and collaborations. Whether
              you have a question or just want to say hello — my inbox is always
              open.
            </p>

            <a href="mailto:arn.k49r@gmail.com" className="btn btn--primary">
              <i className="fa-solid fa-envelope" /> Say Hello
            </a>

            <div className="contact__links">
              <a
                href="mailto:arn.k49r@gmail.com"
                className="contact__link"
              >
                <i className="fa-solid fa-envelope" /> arn.k49r@gmail.com
              </a>
              <a
                href="tel:+917200909197"
                className="contact__link"
              >
                <i className="fa-solid fa-phone" /> +91 7200909197
              </a>
              <a
                href="https://github.com/Arunkumar-eng"
                target="_blank"
                rel="noopener noreferrer"
                className="contact__link"
              >
                <i className="fa-brands fa-github" /> GitHub
              </a>
              <a
                href="https://www.linkedin.com/in/arunkumar49r/"
                target="_blank"
                rel="noopener noreferrer"
                className="contact__link"
              >
                <i className="fa-brands fa-linkedin" /> LinkedIn
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─────── Footer ─────── */}
      <footer className="footer">
        <div className="container">
          <p className="footer__text">
            Designed & Built by <span>Arunkumar R</span> ·{" "}
            {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </>
  );
}

export default App;